/**
 * @sahaj/utils
 * Shared utility functions for Sahaj Connect.
 */

import type { ISODateString, User, UserSummary } from '@sahaj/types';

// ─── Date utilities ───────────────────────────────────────────────────────────

/** Format a timestamp for display in message lists */
export function formatMessageTime(isoDate: ISODateString): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'long' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }
}

/** Format a relative time string (e.g. "2 minutes ago") */
export function formatRelativeTime(isoDate: ISODateString): string {
  const date = new Date(isoDate);
  const diffMs = Date.now() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) return 'just now';
  if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
  if (diffSecs < 86400) return `${Math.floor(diffSecs / 3600)}h ago`;
  return `${Math.floor(diffSecs / 86400)}d ago`;
}

/** Check if two ISO dates are on the same calendar day */
export function isSameDay(a: ISODateString, b: ISODateString): boolean {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
}

/** Format a date as a section divider label */
export function formatDateDivider(isoDate: ISODateString): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

// ─── Mention utilities ────────────────────────────────────────────────────────

const MENTION_REGEX = /@([a-zA-Z0-9_.-]+)/g;

/** Extract all @mentions from a message string */
export function extractMentions(content: string): string[] {
  const matches = [...content.matchAll(MENTION_REGEX)];
  return matches.map((m) => m[1]).filter((m): m is string => m !== undefined);
}

/** Check whether a message content mentions a specific display name */
export function contentMentions(content: string, displayName: string): boolean {
  return content.includes(`@${displayName}`);
}

export interface ContentSegment {
  type: 'text' | 'mention';
  value: string;
  userId?: string;
}

const COMBINED_MENTION_REGEX = /(?:<@([0-9a-fA-F-]{36}|[a-zA-Z0-9_.-]+)>|@([a-zA-Z0-9_.-]+))/g;

export function parseContentSegments(content: string, membersMap?: Record<string, string>): ContentSegment[] {
  const segments: ContentSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  COMBINED_MENTION_REGEX.lastIndex = 0;

  while ((match = COMBINED_MENTION_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', value: content.slice(lastIndex, match.index) });
    }

    const uuid = match[1];
    const rawName = match[2];

    if (uuid) {
      const displayName = membersMap?.[uuid] || (uuid.length === 36 ? 'member' : uuid);
      segments.push({ type: 'mention', value: `@${displayName}`, userId: uuid });
    } else if (rawName) {
      segments.push({ type: 'mention', value: `@${rawName}` });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    segments.push({ type: 'text', value: content.slice(lastIndex) });
  }

  return segments;
}

// ─── User utilities ───────────────────────────────────────────────────────────

/** Get display initials from a user display name */
export function getInitials(displayName: string): string {
  return displayName
    .split(/\s+/)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/** Get a deterministic avatar background color from user ID */
export function getAvatarColor(userId: string): string {
  const colors = [
    '#4F46E5', '#7C3AED', '#DB2777', '#DC2626',
    '#D97706', '#059669', '#0284C7', '#0891B2',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash << 5) - hash + (userId.charCodeAt(i) ?? 0);
    hash |= 0;
  }
  return colors[Math.abs(hash) % colors.length] ?? '#4F46E5';
}

// ─── String utilities ─────────────────────────────────────────────────────────

/** Generate a URL-friendly slug from a name */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 32);
}

/** Truncate a string with ellipsis */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength - 1) + '…';
}

// ─── UUID utilities ───────────────────────────────────────────────────────────

/** Generate a client-side UUID v4 for idempotency keys */
export function generateClientMessageId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── Notification utilities ───────────────────────────────────────────────────

/** Get a human-readable label for a notification type */
export function getNotificationLabel(type: string): string {
  const labels: Record<string, string> = {
    MENTION: 'mentioned you',
    REPLY: 'replied to your message',
    DM: 'sent you a direct message',
    MEMBER_ADDED: 'added you to a workspace',
    WORKSPACE_INVITED: 'invited you to a workspace',
  };
  return labels[type] ?? 'sent you a notification';
}

// ─── User type guard ──────────────────────────────────────────────────────────

export function toUserSummary(user: User): UserSummary {
  const summary: UserSummary = {
    id: user.id,
    displayName: user.displayName,
    status: user.status,
  };
  if (user.avatarUrl !== undefined) {
    summary.avatarUrl = user.avatarUrl;
  }
  return summary;
}

// Re-export types that utils consumers typically need
export type { UserSummary, ISODateString } from '@sahaj/types';
